import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as React from 'react';
import Draggable, { DraggableBounds } from 'react-draggable';

import { ReactComponent as EllipsisVerticalTwoRowsIcon } from '@/assets/icons/ellipsis-vertical-two-rows.svg';
import { ReactComponent as CheckSquare2StackIcon } from '@/assets/icons/Vector.svg';
import { Button } from '@/components/core/Button';
import ButtonPanel from '@/components/core/ButtonPanel/ButtonPanel';
import { cn } from '@/services/utils';

export interface ToolbarTableLabels {
    selectAll?: (total: number) => React.ReactNode;
    delete?: React.ReactNode;
    of?: React.ReactNode;
    selected?: React.ReactNode;
    close?: React.ReactNode;
    /**
     * Full-sentence renderer for the counter in the normal (partial) case.
     * When provided, it replaces the built-in "{selectedCount} of {totalCount}
     * selected" split fragments. Bolding of the numbers is the caller's
     * responsibility.
     */
    partialSelected?: (selectedCount: number, totalCount: number) => React.ReactNode;
    /**
     * Rendered instead of the counter when `allSelected` is true (e.g. Global
     * Select Mode). Bolding of the total number is the caller's responsibility.
     */
    allSelected?: (total: number) => React.ReactNode;
}

export interface ToolbarTableProps {
    selectedCount: number;
    totalCount: number;
    onSelectAll: () => void;
    onDeleteSelected: () => void;

    /**
     * Called when the user clicks the close (X) button. When omitted, the
     * close button (and its preceding divider) are not rendered.
     */
    onClose?: () => void;

    visible?: boolean;
    boundsRef?: React.RefObject<HTMLElement>;
    defaultPosition?: { x: number; y: number };

    /**
     * - `fixed` (default): the toolbar floats at the bottom of the viewport.
     * - `absolute`: the toolbar is positioned at the bottom of its nearest
     *   positioned ancestor. Useful for embedding inside a contained layout
     *   (e.g. Storybook previews).
     */
    position?: 'fixed' | 'absolute';

    labels?: ToolbarTableLabels;

    /** Disables the Select-all action. */
    disableSelectAll?: boolean;

    /** Disables the Delete action. */
    disableDelete?: boolean;

    /**
     * Switches the counter to the "All {Total} selected" variant (using
     * `labels.allSelected`). Typically flipped on when every filtered row
     * is selected (Global Select Mode).
     */
    allSelected?: boolean;

    className?: string;
}

const DRAG_HANDLE_CLASS = 'toolbar-table-drag-handle';

const defaultLabels: Required<ToolbarTableLabels> = {
    selectAll: (total) => (
        <>
            Select all <strong className="tw-font-bold">{total}</strong> entries
        </>
    ),
    delete: 'Delete',
    of: 'of',
    selected: 'selected',
    close: 'Close',
    partialSelected: () => null,
    allSelected: (total) => (
        <>
            All <strong className="tw-font-bold">{total}</strong> selected
        </>
    ),
};

const Divider = () => (
    <div className="tw-w-px tw-h-5 tw-bg-[var(--gray-350)] tw-shrink-0" aria-hidden />
);

const ToolbarTable = React.forwardRef<HTMLDivElement, ToolbarTableProps>(
    (
        {
            selectedCount,
            totalCount,
            onSelectAll,
            onDeleteSelected,
            onClose,
            visible,
            boundsRef,
            defaultPosition,
            position = 'fixed',
            labels,
            disableSelectAll = false,
            disableDelete = false,
            allSelected = false,
            className,
        },
        ref,
    ) => {
        const isVisible = visible ?? selectedCount > 0;
        const nodeRef = React.useRef<HTMLDivElement>(null);
        React.useImperativeHandle(ref, () => nodeRef.current as HTMLDivElement);

        const [bounds, setBounds] = React.useState<DraggableBounds | undefined>();

        React.useLayoutEffect(() => {
            if (!isVisible) return;

            const computeBounds = () => {
                const node = nodeRef.current;
                if (!node) return;

                const rect = node.getBoundingClientRect();
                const containerRect = boundsRef?.current?.getBoundingClientRect();

                if (containerRect) {
                    setBounds({
                        left: containerRect.left - rect.left,
                        top: containerRect.top - rect.top,
                        right: containerRect.right - rect.right,
                        bottom: containerRect.bottom - rect.bottom,
                    });
                } else {
                    setBounds({
                        left: -rect.left,
                        top: -rect.top,
                        right: window.innerWidth - rect.right,
                        bottom: window.innerHeight - rect.bottom,
                    });
                }
            };

            computeBounds();
            window.addEventListener('resize', computeBounds);
            return () => window.removeEventListener('resize', computeBounds);
        }, [isVisible, boundsRef]);

        const l = { ...defaultLabels, ...labels };

        if (!isVisible) return null;

        const positionClass = position === 'fixed' ? 'tw-fixed' : 'tw-absolute';
        const selectAllLabel = l.selectAll(totalCount);
        const selectAllAriaLabel = `Select all ${totalCount} entries`;
        const deleteAriaLabel = typeof l.delete === 'string' ? l.delete : 'Delete selected';
        const closeAriaLabel = typeof l.close === 'string' ? l.close : 'Close toolbar';
        const selectAllTooltip =
            typeof selectAllLabel === 'string' ? selectAllLabel : selectAllAriaLabel;
        const deleteTooltip = typeof l.delete === 'string' ? l.delete : deleteAriaLabel;

        return (
            <Draggable
                nodeRef={nodeRef}
                handle={`.${DRAG_HANDLE_CLASS}`}
                bounds={bounds}
                defaultPosition={defaultPosition}
                positionOffset={{ x: '-50%', y: 0 }}>
                <div
                    ref={nodeRef}
                    role="toolbar"
                    aria-label="Bulk actions"
                    data-name="ToolbarTable"
                    className={cn(
                        positionClass,
                        // Outer wrapper recreates Figma's doubled-border "3D rim":
                        // 1px gray-350 border (via the semantic `tw-border-secondary`
                        // token) + 1px padding sits around the inner white-bordered
                        // Toolbar Container.
                        `tw-left-1/2
                         tw-bottom-6
                         tw-z-50
                         tw-flex
                         tw-flex-col
                         tw-items-stretch
                         tw-border
                         tw-border-solid
                         tw-border-secondary
                         tw-rounded-lg
                         tw-shadow-md
                         tw-select-none`,
                        className,
                    )}>
                    <div
                        data-name="Toolbar Container"
                        // Inner radius is derived dynamically from the outer radius
                        // minus the 1px padding (`tw-p-px`) so the two rims stay
                        // concentric if the design-token `--radius` changes. Today
                        // outer = 8px, so inner = 7px.
                        style={{
                            // 1px gradient border stroke that follows the rounded
                            // corners: the padding-box layer paints the fill-secondary
                            // surface, the border-box layer paints the white -> --gray-100
                            // gradient into the transparent 1px border.
                            background:
                                'linear-gradient(var(--gray-100), var(--gray-100)) padding-box, ' +
                                'linear-gradient(to bottom, #fff, var(--gray-100)) border-box',
                            border: '1px solid transparent',
                        }}
                        className={cn(
                            `tw-flex
                             tw-items-center
                             tw-h-12
                             tw-gap-1
                             tw-px-1
                             tw-bg-fill-secondary
                             tw-rounded-[calc(var(--radius)-1px)]
                             tw-overflow-hidden`,
                        )}>
                        <button
                            type="button"
                            aria-label="Drag toolbar"
                            data-name="Drag Icon"
                            className={cn(
                                DRAG_HANDLE_CLASS,
                                `tw-flex
                                 tw-items-center
                                 tw-justify-center
                                 tw-w-8
                                 tw-h-4
                                 lg:tw-h-5
                                 tw-rounded-full
                                 tw-bg-transparent
                                 tw-border-solid
                                 tw-border-0
                                 tw-cursor-grab
                                 active:tw-cursor-grabbing
                                 focus-visible:!tw-outline-none
                                 focus-visible:tw-border-2
                                 focus-visible:!tw-border-primary-focus`,
                            )}>
                            <span
                                className="tw-flex tw-items-center tw-justify-center tw-icon-primary tw-h-4 tw-w-4 lg:tw-h-5 lg:tw-w-5">
                                <EllipsisVerticalTwoRowsIcon
                                    className="tw-block tw-h-full tw-w-full"
                                />
                            </span>
                        </button>

                        <div
                            data-name="Container"
                            className="tw-flex tw-items-center tw-h-10 tw-gap-1 md:tw-gap-3">
                            <div
                                data-name="numberation"
                                className="tw-flex tw-items-center tw-h-10 tw-gap-1 tw-text-t-sm lg:tw-text-t-base tw-text-t-secondary tw-whitespace-nowrap">
                                {(() => {
                                    if (allSelected) {
                                        return <span data-name="All Selected Text">{l.allSelected(totalCount)}</span>;
                                    }
                                    const partial = labels?.partialSelected?.(selectedCount, totalCount);
                                    if (partial) {
                                        return <span data-name="Partial Selected Text">{partial}</span>;
                                    }
                                    return (
                                        <>
                                            <strong data-name="Selected Count" className="tw-font-bold">{selectedCount}</strong>
                                            <span data-name="Of Text">{l.of}</span>
                                            <strong data-name="Total Count" className="tw-font-bold">{totalCount}</strong>
                                            <span data-name="Selected Text">{l.selected}</span>
                                        </>
                                    );
                                })()}
                            </div>

                            <Divider />

                            <ButtonPanel
                                className="md:tw-hidden"
                                variant="primary"
                                icon={CheckSquare2StackIcon}
                                onClick={onSelectAll}
                                aria-label={selectAllAriaLabel}
                                tooltip={selectAllTooltip}
                                isDisabled={disableSelectAll}
                            />
                            <Button
                                className="tw-hidden md:tw-inline-flex"
                                variant="ghost"
                                hasIcon
                                icon={CheckSquare2StackIcon}
                                onClick={onSelectAll}
                                disabled={disableSelectAll}
                                aria-label={selectAllAriaLabel}>
                                {selectAllLabel}
                            </Button>

                            <Divider />

                            <ButtonPanel
                                className="md:tw-hidden"
                                variant="destructive"
                                icon={TrashIcon}
                                onClick={onDeleteSelected}
                                aria-label={deleteAriaLabel}
                                tooltip={deleteTooltip}
                                isDisabled={disableDelete}
                            />
                            <Button
                                className="tw-hidden md:tw-inline-flex"
                                variant="ghostDestructive"
                                hasIcon
                                icon={TrashIcon}
                                onClick={onDeleteSelected}
                                disabled={disableDelete}
                                aria-label={deleteAriaLabel}>
                                {l.delete}
                            </Button>

                            {onClose && <Divider />}
                        </div>

                        {onClose && (
                            <ButtonPanel
                                variant="primary"
                                icon={XMarkIcon}
                                onClick={onClose}
                                aria-label={closeAriaLabel}
                                isDisabled={false}
                            />
                        )}
                    </div>
                </div>
            </Draggable>
        );
    },
);

ToolbarTable.displayName = 'ToolbarTable';

export { ToolbarTable };
